import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormHelperText } from "@mui/material";
import FormItem from "@components/FormItem/FormItem";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import FormSection from "@components/FormSection/FormSection";
import Input from "@components/Input/Input";
import RepeatableObjectField, {
  type FieldDef,
} from "@components/RepeatableField/RepeatableObjectField";
import {
  mapResourceTypesToOptions,
  type ResourceType,
} from "@features/vessel-call/vessel-call-status/MovementServices/form/utils";
import { samePortValidation } from "@features/vessel-call/vessel-call-status/PortServices/form/utils";
import { type PortServicesOutletContext } from "@features/vessel-call/vessel-call-status/PortServices/PortServicesContainer";
import type { PortServicesFormData } from "@features/vessel-call/vessel-call-status/PortServices/schema/portServicesFormSchema";
import { useFormattedReference } from "@hooks/useFormattedReference";
import { validatePortServiceVesselCall } from "@services/portServicesApi";
import { REFERENCE_DATA_TYPES } from "@services/referenceDataApi";
import { runFieldNavigation } from "@telemetry/runFieldNavigation";
import { Controller, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";

const VESSEL_CALL_SERVICE = "bff-vessel-call";
const RECEIVING_VESSEL_FIELD_NAVIGATION_CONTEXT = {
  module: "vessel_call",
  screen: "Ship Chandling Port Service",
  action: "validate_receiving_vessel_call",
  fromField: "receivingVesselImoNumber",
  toField: "receivingVesselName",
  entityType: "vessel_call",
};

type AdditionalResourceRow = {
  resourceType?: string;
  resourceTypeLabel?: string;
  numberOfResources?: string;
};

const ShipChandlingFields = ({ langBase }: { langBase: string }) => {
  const { t } = useTranslation();
  const { formData, updateFormData, currentVesselImoNumber, selectedPortCode } =
    useOutletContext<PortServicesOutletContext>();
  const [imoValidationMessages, setImoValidationMessages] = useState<string[]>([]);
  const [isEditingReceivingVesselImo, setIsEditingReceivingVesselImo] = useState(false);
  const receivingVesselImoError = formData.formState.errors.receivingVesselImoNumber?.message;
  const isReceivingVesselLookupPending = formData.watch("receivingVesselLookupPending") === true;
  const receivingVesselImoNumber = formData.watch("receivingVesselImoNumber");
  const previousImoRef = useRef(receivingVesselImoNumber);
  const validationRequestRef = useRef(0);
  const normalizedCurrentVesselImoNumber = currentVesselImoNumber?.trim();

  const { options: resourceTypes } = useFormattedReference<ResourceType>(
    REFERENCE_DATA_TYPES.RESOURCE_TYPES_ALL,
    VESSEL_CALL_SERVICE,
  );
  const resourceOptions = useMemo(() => mapResourceTypesToOptions(resourceTypes), [resourceTypes]);

  const setReceivingVesselLookupPending = useCallback(
    (value: boolean) => {
      formData.setValue("receivingVesselLookupPending", value, { shouldValidate: false });
      updateFormData("receivingVesselLookupPending", value);
    },
    [formData, updateFormData],
  );

  const setReceivingVesselImoError = useCallback(
    (messages: string[]) => {
      setImoValidationMessages(messages);
      formData.setError("receivingVesselImoNumber", {
        type: "manual",
        message: messages.join(" "),
      });
    },
    [formData],
  );

  const resetReceivingVesselDetails = useCallback(() => {
    const emptyFields: Array<[keyof PortServicesFormData, string]> = [
      ["receivingVesselId", ""],
      ["receivingVesselName", ""],
      ["receivingVesselCallId", ""],
      ["receivingVesselCallNumber", ""],
    ];

    emptyFields.forEach(([fieldName, value]) => {
      formData.setValue(fieldName, value as never, { shouldDirty: true, shouldValidate: false });
      updateFormData(fieldName, value);
    });
  }, [formData, updateFormData]);

  useEffect(() => {
    if (previousImoRef.current === receivingVesselImoNumber) {
      return;
    }

    previousImoRef.current = receivingVesselImoNumber;
    setImoValidationMessages([]);
    setReceivingVesselLookupPending(false);
    resetReceivingVesselDetails();
    formData.clearErrors(["receivingVesselImoNumber", "receivingVesselName"]);
  }, [
    formData,
    receivingVesselImoNumber,
    resetReceivingVesselDetails,
    setReceivingVesselLookupPending,
  ]);

  const validateReceivingVesselImo = async () => {
    const rawImo = String(formData.getValues("receivingVesselImoNumber") ?? "").trim();
    const validationRequestId = validationRequestRef.current + 1;

    validationRequestRef.current = validationRequestId;
    setImoValidationMessages([]);
    resetReceivingVesselDetails();

    if (!rawImo) {
      setReceivingVesselLookupPending(false);
      setReceivingVesselImoError([
        t("form-validation.required", {
          label: t(`${langBase}.fields.receiving-vessel-imo-number`),
        }),
      ]);
      return;
    }

    if (!/^\d+$/.test(rawImo)) {
      setReceivingVesselLookupPending(false);
      setReceivingVesselImoError([
        t("form-validation.invalid-number", {
          label: t(`${langBase}.fields.receiving-vessel-imo-number`),
        }),
      ]);
      return;
    }

    if (normalizedCurrentVesselImoNumber && rawImo === normalizedCurrentVesselImoNumber) {
      setReceivingVesselLookupPending(false);
      setReceivingVesselImoError([
        t("form-validation.cannot-match-requesting-vessel-imo", {
          field: t(`${langBase}.fields.receiving-vessel-imo-number`),
        }),
      ]);
      return;
    }

    setReceivingVesselLookupPending(true);
    formData.clearErrors(["receivingVesselImoNumber", "receivingVesselName"]);

    try {
      const validationResponse = await runFieldNavigation(
        RECEIVING_VESSEL_FIELD_NAVIGATION_CONTEXT,
        () => validatePortServiceVesselCall(VESSEL_CALL_SERVICE, rawImo),
      );

      if (
        validationRequestRef.current !== validationRequestId ||
        String(formData.getValues("receivingVesselImoNumber") ?? "").trim() !== rawImo
      ) {
        return;
      }

      setReceivingVesselLookupPending(false);

      if (!samePortValidation(validationResponse.port, selectedPortCode)) {
        setReceivingVesselImoError([t("form-validation.imo-does-not-exist")]);
        return;
      }

      const prioritizedValidationMessage = !validationResponse.isExistingVessel
        ? t(`${langBase}.fields.receiving-vessel-not-registered`)
        : !validationResponse.isValidStatus
          ? t(`${langBase}.fields.receiving-vessel-no-active-call`)
          : !validationResponse.isValidCertificate
            ? t(`${langBase}.fields.other-vessel-invalid-certificates`)
            : null;

      if (prioritizedValidationMessage) {
        setReceivingVesselImoError([prioritizedValidationMessage]);
        return;
      }

      if (!validationResponse.vesselCallId) {
        setReceivingVesselImoError([t(`${langBase}.fields.receiving-vessel-validation-failed`)]);
        return;
      }

      setImoValidationMessages([]);
      formData.clearErrors(["receivingVesselImoNumber", "receivingVesselName"]);

      const nextValues: Array<[keyof PortServicesFormData, string]> = [
        ["receivingVesselImoNumber", rawImo],
        ["receivingVesselId", ""],
        ["receivingVesselName", validationResponse.vesselName ?? ""],
        ["receivingVesselCallId", validationResponse.vesselCallId],
        [
          "receivingVesselCallNumber",
          validationResponse.vesselCallNumber ??
            validationResponse.vesselCallNo ??
            validationResponse.vcn ??
            "",
        ],
      ];

      nextValues.forEach(([fieldName, value]) => {
        formData.setValue(fieldName, value as never, { shouldDirty: true, shouldValidate: false });
        updateFormData(fieldName, value);
      });

      await formData.trigger(["receivingVesselImoNumber", "receivingVesselName"]);
    } catch {
      if (validationRequestRef.current === validationRequestId) {
        setReceivingVesselLookupPending(false);
        setReceivingVesselImoError([t(`${langBase}.fields.receiving-vessel-validation-failed`)]);
      }
    }
  };

  const displayedImoMessages =
    isEditingReceivingVesselImo || isReceivingVesselLookupPending
      ? []
      : imoValidationMessages.length
        ? imoValidationMessages
        : receivingVesselImoError
          ? [String(receivingVesselImoError)]
          : [];

  const resourceFields: FieldDef[] = [
    {
      key: "resourceType",
      label: t(`${langBase}.fields.resources-equipment`),
      type: "autocomplete",
      options: resourceOptions,
    },
    {
      key: "numberOfResources",
      label: t(`${langBase}.fields.number-of-resources-equipment`),
      type: "number",
      maxChars: 4,
    },
  ];

  return (
    <FormProvider {...formData}>
      <div className="flex flex-col gap-6" data-testid="ship-chandling-fields">
        <div className="grid grid-cols-2">
          <FormItem<PortServicesFormData, "direction">
            name="direction"
            type="select"
            label={t(`${langBase}.fields.ship-chandling-direction`)}
            testId="ship-chandling-direction"
            onFieldChange={updateFormData}
            options={[
              {
                label: t(`${langBase}.preview.ship-chandling-directions.inbound`),
                value: t(`${langBase}.fields.inbound`),
              },
              {
                label: t(`${langBase}.preview.ship-chandling-directions.outbound`),
                value: t(`${langBase}.fields.outbound`),
              },
            ]}
          />
          <div className="ml-6">
            <FormItem<PortServicesFormData, "rts">
              name="rts"
              type="date-picker"
              label={t(`${langBase}.fields.rts`)}
              testId="ship-chandling-rts"
              onFieldChange={updateFormData}
              timePicker
              combinedInput
              dateConstraint="not-past"
            />
          </div>
        </div>

        <div className="grid grid-cols-4">
          <FormItem<PortServicesFormData, "numberOfPackagesHandled">
            name="numberOfPackagesHandled"
            type="number"
            maxDigits={9}
            label={t(`${langBase}.fields.number-of-packages-handled`)}
            testId="ship-chandling-number-of-packages"
            onFieldChange={updateFormData}
          />
        </div>

        <RepeatableObjectField<PortServicesFormData>
          name="additionalResources"
          sectionLabel={t(`${langBase}.fields.additional-resources-equipment`)}
          label={t(`${langBase}.fields.resources-equipment`)}
          addButtonLabel={t(`${langBase}.fields.add-resource`)}
          fields={resourceFields}
          minItems={1}
          numberOfColumns={2}
          testId="ship-chandling-additional-resources"
          onFieldChange={(_, arr) => {
            const resources = arr as AdditionalResourceRow[];

            updateFormData(
              "additionalResources",
              resources.map((resource) => ({
                resourceType: resource.resourceType ?? "",
                resourceTypeLabel:
                  resourceOptions.find((option) => option.value === resource.resourceType)?.label ??
                  resource.resourceTypeLabel ??
                  "",
                numberOfResources: resource.numberOfResources ?? "",
              })) as PortServicesFormData["additionalResources"],
            );
          }}
        />

        <FormSection title={t(`${langBase}.fields.receiving-vessel-details`)} isFullWidth>
          <div className="grid grid-cols-2">
            <Controller
              name="receivingVesselImoNumber"
              control={formData.control}
              render={({ field }) => (
                <div className="flex flex-col gap-1">
                  <CustomFormLabel>
                    {t(`${langBase}.fields.receiving-vessel-imo-number`)}
                  </CustomFormLabel>
                  <Input
                    {...field}
                    testId="ship-chandling-receiving-vessel-imo"
                    error={displayedImoMessages.length > 0}
                    fullWidth
                    slotProps={{ htmlInput: { inputMode: "numeric" } }}
                    value={field.value ?? ""}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (!/^\d*$/.test(nextValue) || nextValue.length > 8) {
                        return;
                      }
                      setIsEditingReceivingVesselImo(true);
                      field.onChange(nextValue);
                      updateFormData("receivingVesselImoNumber", nextValue);
                    }}
                    onBlur={(event) => {
                      setIsEditingReceivingVesselImo(false);
                      const trimmedValue = event.target.value.trim();
                      if (trimmedValue !== field.value) {
                        field.onChange(trimmedValue);
                        updateFormData("receivingVesselImoNumber", trimmedValue);
                      }
                      field.onBlur();
                      void validateReceivingVesselImo();
                    }}
                  />
                  {displayedImoMessages.map((message: string) => (
                    <FormHelperText key={message} error>
                      {message}
                    </FormHelperText>
                  ))}
                </div>
              )}
            />
            <div className="ml-6">
              <Controller
                name="receivingVesselName"
                control={formData.control}
                render={({ field }) => (
                  <div className="flex flex-col gap-1">
                    <CustomFormLabel disabled>
                      {t(`${langBase}.fields.receiving-vessel-name`)}
                    </CustomFormLabel>
                    <Input
                      {...field}
                      testId="ship-chandling-receiving-vessel-name"
                      fullWidth
                      disabled
                      value={field.value ?? ""}
                    />
                  </div>
                )}
              />
            </div>
          </div>
        </FormSection>
      </div>
    </FormProvider>
  );
};

export default ShipChandlingFields;
